import { useOutletContext } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { GetLegalEntityContractSettingsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import ContractFields from './ContractFields'
import WeeksPerMonthConfiguration from './_WeeksPerMonthConfiguration'

const ContractsLegalEntityPage = () => {
  const { legalEntity, onViewActivity } = useOutletContext<{
    legalEntity: LegalEntity
    onViewActivity: () => void
  }>()

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data, loading } = useQuery(GetLegalEntityContractSettingsDocument, {
    variables: {
      legalEntityId: legalEntity.id,
      companyId: companyId,
      contractVersionType: legalEntity.country,
      featureAvailabilityIds: [Features.compensations_management, Features.payroll_management],
      countries: [legalEntity.country],
    },
  })

  const weeksPerMonthConfiguration = data?.contracts.weeksPerMonthConfigurationsConnection.nodes[0]
  const canUpsertWeeksPerMonthConfiguration =
    data?.contracts._policies.weeksPerMonthConfigurationUpsert.can ?? false
  const contractTemplateId = data?.contracts.contractTemplatesConnection.nodes[0]?.id
  const templateFields = data?.contracts.contractTemplatesConnection.nodes[0]
  const taxonomies = data?.contracts.taxonomies

  const ecctIds = Array.from(
    new Set(
      data?.payroll.expensesCategoriesContractsTaxonomiesConnection.nodes.map(
        (node) => node.contractsTaxonomyId
      )
    )
  )
  const resourceFields = data?.customFields.resourceFieldsConnection.nodes
  const canCreateCustomFields = data?.customFields._policies.companyCreateCustomFields.can ?? false
  const featureAvailabilities = data?.companies?.featureAvailabilitiesConnection?.nodes || []
  const hasCompensationsModule = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.compensations_management
  )
  const hasPayrollModule = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.payroll_management
  )

  const showAdditionalCompensationField = hasPayrollModule

  const hasContractsDataAsJson = data?.features?.featuresConnection.nodes.some(
    (el) => el.name === 'dev_contracts_data_as_json' && el.enabled
  )
  const materializedCountryTemplateFragments =
    data?.contracts.materializedTemplateFragmentsConnection.nodes ?? []

  return (
    <ResponsivePage.Body
      loading={loading}
      error={null}
      showHeader={false}
      onActionClick={(key) => {
        if (key === 'view-activity') onViewActivity()
      }}
    >
      <ContractFields
        legalEntity={legalEntity}
        templateFields={templateFields}
        taxonomies={taxonomies}
        contractTemplateId={contractTemplateId}
        resourceFields={resourceFields || []}
        canCreateCustomFields={canCreateCustomFields}
        ecctIds={ecctIds}
        showAdditionalCompensationField={showAdditionalCompensationField}
        hasContractsDataAsJson={hasContractsDataAsJson}
        materializedCountryTemplateFragments={materializedCountryTemplateFragments}
      />
      {canUpsertWeeksPerMonthConfiguration && (
        <WeeksPerMonthConfiguration
          legalEntity={legalEntity}
          weeksPerMonthConfiguration={weeksPerMonthConfiguration}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default ContractsLegalEntityPage
